import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import dayjs from "dayjs";
import { getDataWithToken } from "../../services/GetDataService";

const tableSx = {
  "& th": {
    padding: "4px 12px",
    fontSize: "12px",
  },
  "& td": {
    padding: "4px 12px",
    fontSize: "12px",
  },
};

const isDateAfterMonths = (createdAt, monthsToAdd) => {
  const newDate = dayjs(createdAt).add(monthsToAdd, "month");
  return newDate.isAfter(dayjs());
};

const productName = (item) => {
  const name = item?.product?.product_data?.[0]?.name || "---------";
  const variation =
    item?.product?.product_variation_data?.[0]?.name || "---------";
  return `${name} ${variation}`;
};

const WarrantyAvailableBadge = ({ available }) =>
  available ? (
    <Button
      variant="outlined"
      color="success"
      size="small"
      disabled
      sx={{
        mt: 0.5,
        minHeight: 22,
        px: 0.75,
        py: 0,
        fontSize: "10px",
        lineHeight: 1.2,
        pointerEvents: "none",
        backgroundColor: "transparent",
        color: "#35b522",
        borderColor: "#35b522",
        "& .MuiButton-endIcon": {
          ml: 0.5,
          mr: 0,
        },
        "&.Mui-disabled": {
          opacity: 1,
          backgroundColor: "transparent",
          color: "#35b522",
          borderColor: "#35b522",
        },
      }}
      endIcon={
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7.5 12L10.5 15L16.5 9M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
            stroke="#35b522"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      }
    >
      Warranty Available
    </Button>
  ) : (
    <Button
      variant="outlined"
      color="error"
      size="small"
      disabled
      sx={{
        mt: 0.5,
        minHeight: 22,
        px: 0.75,
        py: 0,
        fontSize: "10px",
        lineHeight: 1.2,
        pointerEvents: "none",
        backgroundColor: "transparent",
        color: "#D92D20",
        borderColor: "#D92D20",
        "& .MuiButton-endIcon": {
          ml: 0.5,
          mr: 0,
        },
        "&.Mui-disabled": {
          opacity: 1,
          backgroundColor: "transparent",
          color: "#D92D20",
          borderColor: "#D92D20",
        },
      }}
      endIcon={
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15 9L9 15M9 9L15 15M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
            stroke="#D92D20"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      }
    >
      Warranty Not Available
    </Button>
  );

const WarrantyAttachedLists = ({
  repair,
  token,
  logout,
  onNotify,
  refreshKey = 0,
}) => {
  const [loading, setLoading] = useState(false);
  const [attachedList, setAttachedList] = useState([]);
  const [previousWarrantyStockList, setPreviousWarrantyStockList] = useState(
    [],
  );

  const getPreviousProducts = useCallback(
    async (sku) => {
      const url = `/api/v1/stock?sku_number=${parseInt(sku, 10)}`;
      const response = await getDataWithToken(url, token);

      if (response?.status === 401) {
        logout();
        return null;
      }

      if (response?.status >= 200 && response?.status < 300) {
        const products = response?.data?.data || [];
        return products[0] || null;
      }

      onNotify(
        response?.data?.message?.toString() || "Something went wrong",
        "error",
      );
      return null;
    },
    [token, logout, onNotify],
  );

  const enrichWithProduct = useCallback(
    async (items) =>
      Promise.all(
        items.map(async (item) => {
          const product = await getPreviousProducts(item?.sku_number);
          return { ...item, product };
        }),
      ),
    [getPreviousProducts],
  );

  const fetchLists = useCallback(async () => {
    if (!repair?._id) {
      setAttachedList([]);
      setPreviousWarrantyStockList([]);
      return;
    }

    setLoading(true);

    const attachedUrl = `/api/v1/repairAttachedSpareparts?repair_id=${repair._id}&is_warranty_claimed_sku=false&status=true`;
    const warrantyUrl = `/api/v1/repairAttachedSpareparts?repair_id=${repair._id}&is_warranty_claimed_sku=true&status=true`;

    const [attachedResponse, warrantyResponse] = await Promise.all([
      getDataWithToken(attachedUrl, token),
      getDataWithToken(warrantyUrl, token),
    ]);

    if (attachedResponse?.status === 401 || warrantyResponse?.status === 401) {
      logout();
      return;
    }

    if (attachedResponse?.status >= 200 && attachedResponse?.status < 300) {
      const items = attachedResponse?.data?.data || [];
      setAttachedList(await enrichWithProduct(items));
    } else {
      setAttachedList([]);
      onNotify(
        attachedResponse?.data?.message?.toString() ||
          "Failed to load attached list",
        "error",
      );
    }

    if (warrantyResponse?.status >= 200 && warrantyResponse?.status < 300) {
      const items = warrantyResponse?.data?.data || [];
      setPreviousWarrantyStockList(await enrichWithProduct(items));
    } else {
      setPreviousWarrantyStockList([]);
      onNotify(
        warrantyResponse?.data?.message?.toString() ||
          "Failed to load previous warranty stock",
        "error",
      );
    }

    setLoading(false);
  }, [repair?._id, token, logout, onNotify, enrichWithProduct]);

  useEffect(() => {
    fetchLists();
  }, [fetchLists, refreshKey]);

  return (
    <>
      <Box
        sx={{
          mb: 1.5,
          background: "#fff",
          border: "1px solid #EAECF1",
          borderRadius: "12px",
          py: 1.5,
          boxShadow: "0px 1px 2px 0px rgba(15, 22, 36, 0.05)",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "#0F1624",
            fontWeight: 600,
            px: 1.5,
            mb: 1,
          }}
        >
          Attached List ({attachedList?.length || 0})
        </Typography>
        <Box sx={{ overflowX: "auto", minWidth: "100%" }}>
          <TableContainer sx={{ maxHeight: 240 }}>
            <Table stickyHeader aria-label="attached spareparts" sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell style={{ whiteSpace: "nowrap" }}>
                    Product / SKU
                  </TableCell>
                  <TableCell style={{ whiteSpace: "nowrap" }}>
                    Is Warranty Available
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading &&
                  attachedList.length > 0 &&
                  attachedList.map((item) => (
                    <TableRow key={item._id || item.sku_number}>
                      <TableCell sx={{ minWidth: "130px" }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "#0F1624" }}
                        >
                          {productName(item)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          SKU: {item?.sku_number}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          Warranty:{" "}
                          {item?.product?.product_data?.[0]?.warranty ??
                            "---------"}{" "}
                          Months
                        </Typography>
                      </TableCell>
                      <TableCell style={{ whiteSpace: "nowrap" }}>
                        <WarrantyAvailableBadge
                          available={isDateAfterMonths(
                            repair?.created_at,
                            item?.product?.product_data?.[0]?.warranty,
                          )}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                {!loading && attachedList.length < 1 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      No attached spareparts
                    </TableCell>
                  </TableRow>
                )}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {previousWarrantyStockList.length > 0 && (
        <Box
          sx={{
            mb: 1.5,
            background: "#fff",
            border: "1px solid #EAECF1",
            borderRadius: "12px",
            py: 1.5,
            boxShadow: "0px 1px 2px 0px rgba(15, 22, 36, 0.05)",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#0F1624",
              fontWeight: 600,
              px: 1.5,
              mb: 1,
            }}
          >
            Previous Warranty Stock List ({previousWarrantyStockList.length})
          </Typography>
          <Box sx={{ overflowX: "auto", minWidth: "100%" }}>
            <TableContainer sx={{ maxHeight: 240 }}>
              <Table
                stickyHeader
                aria-label="previous warranty stock"
                sx={tableSx}
              >
                <TableHead>
                  <TableRow>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      Product
                    </TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>SKU</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previousWarrantyStockList.map((item) => (
                    <TableRow key={item._id || item.sku_number}>
                      <TableCell sx={{ minWidth: "130px" }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "#0F1624" }}
                        >
                          {productName(item)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: "130px" }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          SKU: {item?.sku_number}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          Claimed on SKU: {item?.claimed_on_sku_number}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}
    </>
  );
};

export default WarrantyAttachedLists;
